import { Button, Card, Radio, RadioChangeEvent, Select } from 'antd';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import chkoCover from '../assets/images/chko.webp';
import geolocationCover from '../assets/images/city/geolocation.jpg';
import heraldryCover from '../assets/images/city/heraldry.png';
import pragueCover from '../assets/images/city/prague.jpg';
import randomCover from '../assets/images/city/random.jpg';
import suggestedCover from '../assets/images/city/suggested.jpg';
import horyCover from '../assets/images/hory.webp';
import kdevkrajiCover from '../assets/images/kdevkraji.webp';
import porovnavaniCover from '../assets/images/porovnavani.webp';
import rekyCover from '../assets/images/reky.webp';
import slepaCover from '../assets/images/slepa.webp';
import tvarCover from '../assets/images/tvar.webp';
import unescoCover from '../assets/images/unesco.webp';
import vetsiCover from '../assets/images/vetsi.webp';
import youtubeCover from '../assets/images/youtube.jpg';
import { GameModeRibbonWrapper } from '../components/GameModeRibbonWrapper';
import { CustomPlace, Geolocation, Random, RandomPlaceInRegion, RegionCity } from '../components/modes';
import { Heraldry } from '../components/modes/Heraldry';
import { routeNames } from '../constants/routes';
import { nutsCodes } from '../enums/nutsCodes';
import { getNextMonthFirstDay, useApiStatus } from '../hooks/useApiStatus';
import useGetRandomUserToken from '../hooks/useGetRandomUserToken';
import useSMapResize from '../hooks/useSMapResize';
import { GameModes } from '../types/game';
import { DisabledSectionOverlay } from './DisabledSectionOverlay';

const { Meta } = Card;
const { Option } = Select;

const ModesContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 30px;
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 1rem;
    animation: fadeInUp 0.8s ease-out 0.4s both;

    @keyframes fadeInUp {
        from {
            opacity: 0;
            transform: translateY(30px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @media (max-width: 767px) {
        gap: 20px;
        padding: 0 0.5rem;
    }
`;

export const SectionModesContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 1.5rem 0;
    /* Breathing room so the heading isn't glued to viewport top after smooth-scroll. */
    scroll-margin-top: 24px;

    h1 {
        font-family: 'Work Sans', Arial, sans-serif;
        font-size: clamp(1.4rem, 3vw, 1.85rem);
        font-weight: 700;
        color: #222;
        margin-bottom: 1.5rem;
        text-align: center;
    }

    @media (max-width: 767px) {
        padding: 1rem 0;

        h1 {
            margin-bottom: 1rem;
        }
    }
`;

const QuickNav = styled.nav`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
    padding: 4px 12px 0 12px;
`;

const QuickNavItem = styled.button`
    background: #ffffff;
    border: 1px solid #e8e8e8;
    border-radius: 999px;
    padding: 7px 14px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    color: #595959;
    cursor: pointer;
    transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, transform 0.12s ease;

    &:hover {
        border-color: #c80707;
        color: #c80707;
        background: #fff5f5;
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }

    &:focus-visible {
        outline: 2px solid #ffd6d6;
        outline-offset: 2px;
    }
`;

const QuickNavLabel = styled.span`
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #8c8c8c;
    margin-right: 6px;
    align-self: center;

    @media (max-width: 600px) {
        width: 100%;
        text-align: center;
        margin: 0 0 4px 0;
    }
`;

const scrollToSection = (id: string) => {
    if (typeof document === 'undefined') return;
    const el = document.getElementById(id);
    if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
};

const SectionModeCards = styled.div`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 1.5rem;
    gap: 20px;
    width: 100%;

    @media (max-width: 1024px) {
        gap: 15px;
    }

    @media (max-width: 767px) {
        flex-direction: column;
        align-items: center;
        gap: 15px;
    }
`;

// Wrap so DisabledSectionOverlay can position absolutely over the cards but the
// section heading (h1) stays visible above and clickable. `isolation: isolate`
// creates a fresh stacking context so child Cards don't escape the overlay.
const SectionModeCardsRelative = styled.div`
    position: relative;
    isolation: isolate;
    width: 100%;
    display: flex;
    justify-content: center;
    border-radius: 12px;
`;

const renderTiles = (modes: GameModes[]) =>
    modes.map(mode => {
        const { coverImgAlt, coverImgSrc, title, content, isNew } = mode;
        return (
            <GameModeRibbonWrapper key={title} condition={!!isNew} message="Novinka!">
                <Card cover={<img alt={coverImgAlt} src={coverImgSrc} style={{ opacity: 0.9 }} />}>
                    <Meta title={title} description={content} />
                </Card>
            </GameModeRibbonWrapper>
        );
    });

const renderSection = (id: string, title: string, modes: GameModes[], overlay?: React.ReactNode) => {
    if (modes.length === 0) {
        return null;
    }
    return (
        <SectionModesContainer id={id}>
            <h1>{title}</h1>
            <SectionModeCardsRelative>
                <SectionModeCards>{renderTiles(modes)}</SectionModeCards>
                {overlay}
            </SectionModeCardsRelative>
        </SectionModesContainer>
    );
};

const SECTIONS = [
    { id: 'panorama', label: 'Panoráma' },
    { id: 'mapa-regiony', label: 'Mapa a regiony' },
    { id: 'priroda', label: 'Příroda' },
    { id: 'hry-hadanky', label: 'Hry a hádanky' },
    { id: 'symboly-pamatky', label: 'Symboly a památky' },
    { id: 'ostatni', label: 'Ostatní' },
];

export const ModesOverview = () => {
    useGetRandomUserToken();
    const { width } = useSMapResize();
    const history = useHistory();
    const apiStatus = useApiStatus();
    const [randomMode, setRandomMode] = useState('cr');
    const [regionNutCode, setRegionNutCode] = useState(null);

    const isMultiplayerSupported = width > 599;
    const panoramaDisabled = apiStatus === 'error';

    const handleRandomModeChange = (e: RadioChangeEvent) => {
        setRandomMode(e.target.value);
    };

    const regionEnumKeys = Object.keys(nutsCodes);

    const panoramaModes: GameModes[] = useMemo(() => {
        return [
            {
                coverImgAlt: 'Herní mód - Vlastní místo',
                coverImgSrc: suggestedCover,
                title: 'Vlastní místo',
                content: (
                    <>
                        <p>
                            Zahraj si tam, kde to dobře znáš. Vyhledej své město, obec či jiné zajímavé místo, jehož
                            okolí chceš blíže prozkoumat, a vyraž na cestu!
                        </p>
                        <CustomPlace multiplayerSupported={isMultiplayerSupported} />
                    </>
                ),
            },
            {
                coverImgAlt: 'Herní mód - Náhodné místo',
                coverImgSrc: randomCover,
                title: 'Náhodné místo',
                content: (
                    <>
                        <div className="randomPlace__modes">
                            <Radio.Group value={randomMode} onChange={handleRandomModeChange} buttonStyle="solid">
                                <Radio.Button value="cr">celá ČR</Radio.Button>
                                <Radio.Button value="nut">kraj</Radio.Button>
                            </Radio.Group>
                        </div>
                        {randomMode === 'cr' ? (
                            <>
                                <p>
                                    Nech se přenést do jedné z 6259 obcí ČR a jejího blízkého okolí. V každém kole na
                                    tebe čeká jiné náhodné místo. Tahle hra je pro experty, co mají ČR projetou křížem
                                    krážem.
                                </p>
                                <Random multiplayerSupported={isMultiplayerSupported} />
                            </>
                        ) : (
                            <>
                                <div style={{ marginBottom: '10px' }}>
                                    <p>
                                        Krajské město už je ti malé, ale zároveň nechceš tak úplně bloudit? Vyber si
                                        jeden ze <b>14 krajů</b> v Česku a zase máš co objevovat.
                                    </p>
                                    <div className="randomPlace__modes">
                                        <label htmlFor="city">Kraj: </label>
                                        <Select
                                            size="large"
                                            showSearch
                                            style={{ width: 200 }}
                                            placeholder="zvolit kraj"
                                            onChange={value => {
                                                setRegionNutCode(value);
                                            }}
                                        >
                                            {regionEnumKeys.map(key => {
                                                // @ts-ignore
                                                const { code, name } = nutsCodes[key];
                                                return <Option value={code}>{name}</Option>;
                                            })}
                                        </Select>
                                    </div>
                                </div>
                                <RandomPlaceInRegion
                                    multiplayerSupported={isMultiplayerSupported}
                                    regionNutCode={regionNutCode}
                                />
                            </>
                        )}
                    </>
                ),
            },
            {
                coverImgAlt: 'Herní mód - Krajská města ČR',
                coverImgSrc: pragueCover,
                title: 'Krajská města ČR',
                content: (
                    <>
                        <p>
                            Virtuální procházka po jednom z krajských sídel. Vydej se na poznávačku krajských měst.
                            Třeba objevíš zákoutí, kam se budeš chtít podívat i naživo.
                        </p>
                        <RegionCity multiplayerSupported={isMultiplayerSupported} />
                    </>
                ),
            },
            {
                coverImgAlt: 'Herní mód - Podle mojí geolokace',
                coverImgSrc: geolocationCover,
                title: 'Podle mojí geolokace',
                content: (
                    <>
                        <p>
                            Zaměřit polohu svého zařízení a ukázat, kdo je pánem a znalcem svého bezprostředního okolí.
                            To zní jako plán!
                        </p>
                        <Geolocation />
                    </>
                ),
            },
        ];
    }, [randomMode, regionNutCode, isMultiplayerSupported]);

    const mapAndRegionsModes: GameModes[] = useMemo(() => {
        return [
            {
                coverImgAlt: 'Herní mód - Slepá mapa obcí',
                coverImgSrc: slepaCover,
                title: 'Slepá mapa obcí',
                content: (
                    <>
                        <p>
                            Klikni do mapy České republiky a odhadni, kde leží zadaná obec. Skóre určuje vzdálenost
                            tvého tipu od skutečné polohy.
                        </p>
                        <div className="game-start-button-group">
                            <Button
                                size="large"
                                type="primary"
                                className="button-play"
                                onClick={() => history.push(`/${routeNames.slepaMapaObci}`)}
                            >
                                Hrát
                            </Button>
                        </div>
                    </>
                ),
            },
            {
                coverImgAlt: 'Herní mód - Obec do kraje a okresu',
                coverImgSrc: kdevkrajiCover,
                title: 'Obec → kraj a okres',
                content: (
                    <>
                        <p>
                            Dostaneš název české obce a hádáš, do kterého kraje nebo okresu patří. Krátké kvízové kolo
                            se čtyřmi možnostmi.
                        </p>
                        <div className="game-start-button-group">
                            <Button
                                size="large"
                                type="primary"
                                className="button-play"
                                onClick={() => history.push(`/${routeNames.obecKrajOkres}`)}
                            >
                                Hrát
                            </Button>
                        </div>
                    </>
                ),
            },
            {
                coverImgAlt: 'Herní mód - Tvar kraje',
                coverImgSrc: tvarCover,
                title: 'Tvar kraje',
                content: (
                    <>
                        <p>
                            Poznej český kraj jenom podle jeho obrysu. Vybíráš vždy ze čtyř možností. Skvělý trénink
                            slepé mapy.
                        </p>
                        <div className="game-start-button-group">
                            <Button
                                size="large"
                                type="primary"
                                className="button-play"
                                onClick={() => history.push(`/${routeNames.tvarKraje}`)}
                            >
                                Hrát
                            </Button>
                        </div>
                    </>
                ),
            },
        ];
    }, [history]);

    const natureModes: GameModes[] = useMemo(() => {
        return [
            {
                coverImgAlt: 'Herní mód - Hory a vrcholy',
                coverImgSrc: horyCover,
                title: 'Hory a vrcholy',
                content: (
                    <>
                        <p>
                            Přes 1300 pojmenovaných vrcholů nad 800 m. Hádej kraj, pohoří, slepou mapu nebo poznej,
                            který ze tří je nejvyšší. Data: OpenStreetMap + Wikipedie.
                        </p>
                        <div className="game-start-button-group">
                            <Button
                                size="large"
                                type="primary"
                                className="button-play"
                                onClick={() => history.push(`/${routeNames.horyAVrcholy}`)}
                            >
                                Hrát
                            </Button>
                        </div>
                    </>
                ),
            },
            {
                coverImgAlt: 'Herní mód - Řeky',
                coverImgSrc: rekyCover,
                title: 'Řeky',
                content: (
                    <>
                        <p>
                            100 nejdelších řek tekoucích Českem. Slepá mapa pramenů, kam řeka ústí, nebo „která ze tří
                            je nejdelší". Data: Wikipedie.
                        </p>
                        <div className="game-start-button-group">
                            <Button
                                size="large"
                                type="primary"
                                className="button-play"
                                onClick={() => history.push(`/${routeNames.reky}`)}
                            >
                                Hrát
                            </Button>
                        </div>
                    </>
                ),
            },
            {
                coverImgAlt: 'Herní mód - Národní parky a CHKO',
                coverImgSrc: chkoCover,
                title: 'Národní parky a CHKO',
                content: (
                    <>
                        <p>
                            4 národní parky a 27 chráněných krajinných oblastí v Česku. Hádej kraj nebo umisťuj chráněné
                            území na slepou mapu. Data: AOPK ČR.
                        </p>
                        <div className="game-start-button-group">
                            <Button
                                size="large"
                                type="primary"
                                className="button-play"
                                onClick={() => history.push(`/${routeNames.chranenaUzemi}`)}
                            >
                                Hrát
                            </Button>
                        </div>
                    </>
                ),
            },
        ];
    }, [history]);

    const gamesAndPuzzlesModes: GameModes[] = useMemo(() => {
        return [
            {
                coverImgAlt: 'Herní mód - Co je větší?',
                coverImgSrc: vetsiCover,
                title: 'Co je větší?',
                content: (
                    <>
                        <p>
                            Dostaneš dvě české obce a vybíráš tu s vyšším počtem obyvatel. Sbíráš streak - jediná chyba
                            znamená konec. Data ČSÚ.
                        </p>
                        <div className="game-start-button-group">
                            <Button
                                size="large"
                                type="primary"
                                className="button-play"
                                onClick={() => history.push(`/${routeNames.coJeVetsi}`)}
                            >
                                Hrát
                            </Button>
                        </div>
                    </>
                ),
            },
        ];
    }, [history]);

    const symbolsAndMonumentsModes: GameModes[] = useMemo(() => {
        return [
            {
                coverImgAlt: 'Herní mód - Heraldika',
                coverImgSrc: heraldryCover,
                title: 'Erby měst a obcí v Česku',
                content: (
                    <>
                        <p>
                            Prověř své znalosti heraldiky a uhádni, který erb patří kterému městu nebo obci v Česku.
                            Zábavná výzva pro všechny, kteří se zajímají o historii a symboliku českých měst.
                        </p>
                        <Heraldry />
                    </>
                ),
            },
            {
                coverImgAlt: 'Herní mód - UNESCO památky',
                coverImgSrc: unescoCover,
                title: 'UNESCO památky',
                content: (
                    <>
                        <p>
                            17 českých památek UNESCO. Poznáváš podle fotky, hádáš kraj nebo umisťuješ svůj tip na
                            slepou mapu. Fotky z Wikimedia Commons.
                        </p>
                        <div className="game-start-button-group">
                            <Button
                                size="large"
                                type="primary"
                                className="button-play"
                                onClick={() => history.push(`/${routeNames.unesco}`)}
                            >
                                Hrát
                            </Button>
                        </div>
                    </>
                ),
            },
        ];
    }, [history]);

    const otherModes: GameModes[] = useMemo(() => {
        return [
            {
                coverImgAlt: 'Herní mód - Videa',
                coverImgSrc: youtubeCover,
                title: "Let's play videa",
                content: (
                    <>
                        <p>
                            Dneska se ti nechce nikam klikat? Tak nech hraní na druhých a jen se koukej! Komunita
                            skvělých fanoušků natáčí, vtipně komentuje a sdílí svá videa z toulání se po mapě na{' '}
                            <b>YouTube</b>.
                        </p>
                        <h3>YouTube kanály</h3>
                        <ul className="video-list">
                            <li>
                                {/* eslint-disable-next-line react/jsx-no-target-blank */}
                                <a
                                    href="https://www.youtube.com/@kdepakjsme7888/videos"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Kdepak jsme?
                                </a>
                            </li>
                            <li>
                                {/* eslint-disable-next-line react/jsx-no-target-blank */}
                                <a
                                    href="https://www.youtube.com/@LakelyDorton/videos"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    LakelyDorton
                                </a>
                            </li>
                        </ul>
                    </>
                ),
            },
        ];
    }, []);

    return (
        <ModesContainer>
            <QuickNav aria-label="Rychlá navigace na sekce herních módů">
                <QuickNavLabel>Skoč na:</QuickNavLabel>
                {SECTIONS.map(s => (
                    <QuickNavItem key={s.id} type="button" onClick={() => scrollToSection(s.id)}>
                        {s.label}
                    </QuickNavItem>
                ))}
            </QuickNav>
            {renderSection(
                'panorama',
                'Herní módy s panoramaty',
                panoramaModes,
                panoramaDisabled ? <DisabledSectionOverlay resumeDate={getNextMonthFirstDay()} /> : null,
            )}
            {renderSection('mapa-regiony', 'Mapa a regiony', mapAndRegionsModes)}
            {renderSection('priroda', 'Příroda', natureModes)}
            {renderSection('hry-hadanky', 'Hry a hádanky', gamesAndPuzzlesModes)}
            {renderSection('symboly-pamatky', 'Symboly a památky', symbolsAndMonumentsModes)}
            {renderSection('ostatni', 'Ostatní', otherModes)}
        </ModesContainer>
    );
};
